import type { AttributeDefinition, AttributeType, Library } from '@/shared/helpers/api/types/entities';
import type { CodeSearchHit, CodeSearchRequest, CodeSearchResponse } from '@/shared/helpers/api';
import type { AttributeValue } from '@/shared/codeedit/codeEditFields';
import { getNameLabel } from '@/shared/helpers/nameWithAliasHelper';

export type IdentityField = {
    /** `name`, or the attribute definition name. */
    field: string;
    label: string;
    type: AttributeType | 'Name';
    required: boolean;
    choices: string[];
};

/** The search terms entered in the find form, by identity field. */
export type SearchTerms = Record<string, string>;

export type FindOutcome = {
    /** The exact item the identity points at, when it exists. */
    exactMatch: CodeSearchHit | null;
    canCreate: boolean;
    message: string;
    /** Why the item cannot be created, in the words saving would use. */
    reasons: string[];
    messageType: 'is-info' | 'is-warning' | 'is-success';
};

const WILDCARD = /[*%]/;

export function hasWildcard(value?: string): boolean {
    return !!value && WILDCARD.test(value);
}

/**
 * The fields that identify an item in the library, in the order the item editor shows them:
 * the name (when it is part of the identity) followed by the identity attributes.
 */
export function getIdentityFields(library: Pick<Library, 'nameInIdentity' | 'nameAlias' | 'attributeDefinitions'>): IdentityField[] {
    const fields: IdentityField[] = [];

    if (library.nameInIdentity) {
        fields.push({ field: 'name', label: getNameLabel(library.nameAlias), type: 'Name', required: true, choices: [] });
    }

    const attributes = (library.attributeDefinitions ?? [])
        .filter((ad: AttributeDefinition) => ad.includeInIdentity)
        .toSorted((a, b) => a.sequenceNumber - b.sequenceNumber);

    for (const ad of attributes) {
        fields.push({
            field: ad.name,
            label: ad.displayAs || ad.name,
            type: ad.attributeType,
            required: ad.required,
            choices: (ad.choices ?? []).map(c => String(c.value))
        });
    }

    return fields;
}

export function isSearchEmpty(terms: SearchTerms): boolean {
    return Object.values(terms).every(v => (v ?? '').replace(/[*%]/g, '').trim() === '');
}

export function buildSearchRequest(codeSetName: string, identityFields: IdentityField[], terms: SearchTerms): CodeSearchRequest {
    const attributes: Record<string, string> = {};
    for (const f of identityFields) {
        if (f.field !== 'name') {
            attributes[f.field] = (terms[f.field] ?? '').trim();
        }
    }

    return {
        codeSetName,
        name: (terms.name ?? '').trim(),
        attributes
    };
}

/**
 * Decides what the user is offered after a search. The user always chooses the action: a wildcard search only
 * lists matches, and creating is only offered for a complete identity that no item has yet. Whether saving would
 * accept the new item is only checked when the user selects Create; `createErrors` are the reasons from that check,
 * or null before it has run.
 */
export function getFindOutcome(response: CodeSearchResponse, terms: SearchTerms, createErrors: string[] | null = null): FindOutcome {
    const outcome = (exactMatch: CodeSearchHit | null, canCreate: boolean, message: string, messageType: FindOutcome['messageType'], reasons: string[] = []): FindOutcome =>
        ({ exactMatch, canCreate, message, reasons, messageType });

    const searchedWildcard = Object.values(terms).some(hasWildcard);
    if (searchedWildcard) {
        return response.codes.length === 0
            ? outcome(null, false, 'No matching items. Remove the wildcard to create a new item.', 'is-warning')
            : outcome(null, false, 'Select the item to edit. Remove the wildcard to create a new item.', 'is-info');
    }

    if (response.exactMatch) {
        return response.exactMatch.isValid
            ? outcome(response.exactMatch, false, 'The item exists and can be updated.', 'is-success')
            : outcome(response.exactMatch, false, 'The item exists, but is invalid. It can be updated, but not created again.', 'is-warning');
    }

    if (response.exactIdentity && createErrors && createErrors.length > 0) {
        return outcome(null, false, 'The item does not exist, and cannot be created with these values:', 'is-warning', createErrors);
    }

    if (response.exactIdentity) {
        return outcome(null, true, 'The item does not exist.', 'is-info');
    }

    return outcome(null, false, 'Fill in all required identity fields to create a new item.', 'is-warning');
}

/** The identity values to start a new item with, converted to the types the item editor expects. */
export function toTemplateValues(identityFields: IdentityField[], terms: SearchTerms): Record<string, AttributeValue> {
    const template: Record<string, AttributeValue> = {};

    for (const f of identityFields) {
        const raw = (terms[f.field] ?? '').trim();
        if (raw === '') {
            continue;
        }

        switch (f.type) {
            case 'Int':
            case 'Float':
                template[f.field] = Number(raw);
                break;
            case 'Bool':
                template[f.field] = raw.toLowerCase() === 'true';
                break;
            default:
                template[f.field] = raw;
        }
    }

    return template;
}
